import { useCallback, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import { CountryCodeList } from 'types/countries';
import { defaultCACityPayload, defaultUSCityPayload } from 'constants/locations';
import { DEFAULT_EXPIRE_DAYS, LOCATION_EXPIRE_DAYS, SITE_LOCATION_COOKIE_NAME, USER_LOCATION_COOKIE_NAME } from 'constants/cookies';
import { useFeaturesContext } from 'contexts/features';
import { useThemeContext, useModalContext } from 'contexts';
import { handleQuebecPopup, IsUserInQuebec } from 'utils/modals';
import { themeNameToTenantId } from '@tenants';
import useDebounce from './use-debounce';

import type { IUserLocation } from 'utils/user-location';
import type { CountryCode } from 'types/countries';
import type { SearchSuggestions } from 'components/suggested-location-dropdown';

export interface SiteLocationState {
  siteLocation: CountryCode | undefined;
  userLocation: IUserLocation | undefined;
  userLocationSameAsSiteLocation: boolean;
  userPopularCities: SearchSuggestions[];
  handleSetSiteLocation: (countryCode: CountryCode) => void;
  handleSetDefaultUserLocation: (countryCode: CountryCode) => void;
}

interface SiteLocationProps {
  storedUserLocation?: IUserLocation;
  storedSiteLocation?: CountryCode;
  storedUserPopularCities?: SearchSuggestions[];
  functionalCookiesEnabled?: boolean;
}

export function useSiteLocation({
  storedUserLocation,
  storedSiteLocation,
  storedUserPopularCities,
  functionalCookiesEnabled,
}: SiteLocationProps): SiteLocationState {
  const { features } = useFeaturesContext();
  const { themeName } = useThemeContext();
  const { openModal } = useModalContext();

  const [siteLocation, setSiteLocation] = useState<CountryCode | undefined>(storedSiteLocation);
  const [userLocation, setUserLocation] = useState<IUserLocation | undefined>(storedUserLocation);
  const userPopularCities = storedUserPopularCities || [];
  const userLocationSameAsSiteLocation = userLocation?.countryCode === siteLocation;

  const handleSetSiteLocation = useCallback((location: CountryCode) => {
    Cookies.set(SITE_LOCATION_COOKIE_NAME, location, { expires: LOCATION_EXPIRE_DAYS });
    setSiteLocation(location);
  }, []);

  const handleSetDefaultUserLocation = useCallback((country: CountryCode) => {
    const location = country === CountryCodeList.UNITED_STATES ? defaultUSCityPayload : defaultCACityPayload;
    Cookies.set(USER_LOCATION_COOKIE_NAME, JSON.stringify(location), { expires: DEFAULT_EXPIRE_DAYS });
    setUserLocation(location);
  }, []);

  const isSupportedCountryCode = useCallback((countryCode = CountryCodeList.CANADA) => {
    return countryCode === CountryCodeList.CANADA || countryCode === CountryCodeList.UNITED_STATES;
  }, []);

  useEffect(() => {
    if (!siteLocation) {
      if (!features?.useUsListings) {
        handleSetSiteLocation(CountryCodeList.CANADA);
      } else {
        if (userLocation?.countryCode === CountryCodeList.UNITED_STATES) {
          handleSetSiteLocation(CountryCodeList.UNITED_STATES);
        } else if (userLocation?.countryCode) {
          handleSetSiteLocation(CountryCodeList.CANADA);
        }
      }
    }
  }, [userLocation, siteLocation, features?.useUsListings, handleSetSiteLocation]);

  useEffect(() => {
    if (functionalCookiesEnabled && storedUserLocation && isSupportedCountryCode(storedUserLocation?.countryCode)) {
      Cookies.set(USER_LOCATION_COOKIE_NAME, JSON.stringify(storedUserLocation), { expires: DEFAULT_EXPIRE_DAYS });
      setUserLocation(storedUserLocation);
    } else if (siteLocation === CountryCodeList.UNITED_STATES) {
      setUserLocation(defaultUSCityPayload);
    } else {
      setUserLocation(defaultCACityPayload);
    }
  }, [storedUserLocation, functionalCookiesEnabled, siteLocation, isSupportedCountryCode]);

  useEffect(() => {
    if (storedSiteLocation) {
      handleSetSiteLocation(storedSiteLocation);
    }
  }, [storedSiteLocation, handleSetSiteLocation]);

  const debouncedHandleQuebecPopup = useDebounce(() => {
    const isUserInQuebec = IsUserInQuebec(userLocation);
    handleQuebecPopup({ tenantId: themeNameToTenantId(themeName), isQuebec: isUserInQuebec, openModal, source: 'homepage' });
  }, 300);

  useEffect(() => {
    debouncedHandleQuebecPopup();
  }, [themeName, userLocation, openModal, debouncedHandleQuebecPopup]);

  return {
    siteLocation,
    userLocation,
    userLocationSameAsSiteLocation,
    userPopularCities,
    handleSetSiteLocation,
    handleSetDefaultUserLocation,
  };
}
